import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, TouchableOpacity } from 'react-native';

import MessageParts from '@components/Chat/MessageParts';
import useMe from '@hooks/useMe';
import { useAppNavigation } from '@navigation/useAppNavigation';
import theme from '@themes/theme';
import { triggersConfig } from '@utils/mentions';

import ActivityEventBadge from './ActivityEventBadge';
import { getActorDisplayName } from './activityHelpers';
import Avatar from '../../components/Avatar/Avatar';
import Icon from '../../components/shared/Icon/Icon';
import { User } from '../../graphql/generated';
import { Box, Text } from '../Restyle';

interface ActivityItemProps {
  user?: User | null;
  message: string;
  needBoldString?: string[];
  createdAt: Date;
  index: number;
  children: ReactNode;
  /** The audit event string used to render the correct badge icon */
  event?: string;
  onChatPress?: () => void;
  filterVal?: string;
  linkUserInMessage?: User;
  externalNavigate?: (...args: any[]) => void;
  noMargin?: boolean;
  /** When false (last item in its date group), the bottom separator is hidden */
  showBottomSeparator?: boolean;
  // isProject?: boolean;
}

export const ActivityItem: React.FC<ActivityItemProps> = ({
  user,
  message,
  needBoldString,
  createdAt,
  index,
  children,
  event,
  filterVal,
  onChatPress,
  linkUserInMessage,
  externalNavigate,
  showBottomSeparator = true,
  // isProject = false,
}) => {
  const nav = useAppNavigation();
  const { me } = useMe();
  const [isNameHovered, setIsNameHovered] = useState(false);

  const handleActorPress = () => {
    if (!user?.id) return;
    if (user.id === me?.id) {
      (nav as any).navigate('my-account');
    } else {
      nav.navigateToContact({ id: user.id });
    }
  };

  // Show only the time (e.g. "04:09 PM") — the date is already in the
  // sticky section header above the group.
  const formattedTime = new Date(createdAt).toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const { t } = useTranslation();
  const displayName = getActorDisplayName(user, t('shared:unknownUser'));

  return (
    // Outer row: [avatar col] [content col]
    // The separator border sits only on the content column so it does not
    // extend under the avatar.
    <Box
      flexDirection='row'
      testID={`ActivityItem-${index}`}
      pb='s'
      paddingHorizontal='m'
      backgroundColor='white'>
      {/* ── Left column: avatar + event badge ── */}
      <Pressable
        onPress={handleActorPress}
        disabled={!user?.id}
        accessibilityRole='button'
        accessibilityLabel={`View ${displayName}'s profile`}
        style={({ focused }) => [
          styles.avatarPressable,
          user?.id ? (styles.cursorPointer as any) : undefined,
          focused && (styles.focusRing as any),
        ]}>
        <Box style={styles.avatarWrapper}>
          <Avatar
            id={user?.id ?? ''}
            avatar={user?.avatar ?? null}
            label={displayName}
            size='medium'
            disableAvatarPress
          />
          {!!event && <ActivityEventBadge event={event} />}
        </Box>
      </Pressable>

      {/* ── Right column: name + time, then message, then children ──
           The hairline separator is placed here so it does not run
           beneath the avatar. */}
      <Box
        flex={1}
        marginLeft='s'
        pt='xs'
        pb={showBottomSeparator ? 'xs' : 'none'}
        style={showBottomSeparator ? styles.contentWithSeparator : undefined}>
        {/* Top row: name on the left, time 8px to its right */}
        <Box flexDirection='row' alignItems='center' mb='xs'>
          <Pressable
            onPress={handleActorPress}
            disabled={!user?.id}
            accessibilityRole='button'
            accessibilityLabel={`View ${displayName}'s profile`}
            onHoverIn={() => setIsNameHovered(true)}
            onHoverOut={() => setIsNameHovered(false)}
            style={({ focused }) => [
              user?.id ? (styles.cursorPointer as any) : undefined,
              focused && (styles.focusRing as any),
            ]}>
            <Text
              variant='labelEmphasized'
              color='textPrimary'
              style={[
                styles.nameText,
                isNameHovered && (styles.nameHovered as any),
              ]}>
              {displayName}
            </Text>
          </Pressable>
          <Text variant='metadata' color='grey04' style={styles.timeText}>
            {formattedTime}
          </Text>
          {!!onChatPress && (
            <TouchableOpacity onPress={onChatPress} style={styles.chatBtn}>
              <Icon name='CornerUpLeft' color='black' variant='s' />
            </TouchableOpacity>
          )}
        </Box>

        {/* Message row */}
        {message && message.length > 0 && (
          <Box mb='xxs'>
            <MessageParts
              value={message}
              needBoldString={needBoldString}
              configs={[triggersConfig.mention]}
              styleLinks
              navigateToChatOnMentionPress
              filterVal={filterVal}
              linkUserInMessage={linkUserInMessage}
              externalNavigate={externalNavigate}
            />
          </Box>
        )}

        {/* Attachment / tag children */}
        <Box mb='xs'>{children}</Box>
      </Box>
    </Box>
  );
};

const styles = StyleSheet.create({
  // Wraps the avatar so the absolutely-positioned badge stays within bounds
  avatarWrapper: {
    position: 'relative',
    width: 40,
    height: 40,
  },
  // Pressable wrapper for the avatar — expands touch target to 44×44
  avatarPressable: {
    alignSelf: 'flex-start',
    minWidth: 44,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Separator on the content column only — does not extend under the avatar
  contentWithSeparator: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: theme.colors.grey02,
  },
  nameText: {
    flexShrink: 1,
  },
  nameHovered: {
    textDecorationLine: 'underline',
  },
  focusRing: {
    outline: '2px solid',
    outlineColor: theme.colors.greenBrand,
    borderRadius: theme.borderRadii.xs,
  },
  // 8px gap between name and time
  timeText: {
    marginLeft: theme.spacing.xs,
    flexShrink: 0,
  },
  chatBtn: {
    marginLeft: theme.spacing.xs,
    flexShrink: 0,
  },
  // Web-only: pointer cursor for tappable actor targets
  cursorPointer: {
    cursor: 'pointer',
  },
});
